import PropTypes from 'prop-types';
import React from 'react';

import FragmentVisualization from 'molbio/components/visualizations/FragmentVisualization';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

import {ErrorMessage, ErrorMessages, Warning, Warnings} from '../../BulkWorkflowComponents';
// @ts-expect-error
// eslint-disable-next-line unused-imports/no-unused-imports
import * as BulkAssemblyActions from '../BulkAssemblyActions';
import * as BulkAssemblyConstants from '../BulkAssemblyConstants';
import * as BulkAssemblyUtil from '../BulkAssemblyUtil';
import GibsonPrimerSummary from './GibsonPrimerSummary';
import ValidationStatus from './ValidationStatus';

const STICKY = 'STICKY';
const OVERLAP = 'OVERLAP';

function getLeftType(sequences: Array<BulkAssemblyConstants.Sequence>): string | null {
  for (const sequence of sequences) {
    if (sequence.fivePrimeSticky) {
      return STICKY;
    } else if (sequence.fivePrimeOverlap) {
      return OVERLAP;
    }
  }
  return null;
}

function getLeftHeaderContents(endpointType: string | null): string | null {
  if (endpointType === STICKY) {
    return 'Left Sticky';
  } else if (endpointType === OVERLAP) {
    return 'Left Overlap';
  }
  return null;
}

function getRightType(sequences: Array<BulkAssemblyConstants.Sequence>): string | null {
  for (const sequence of sequences) {
    if (sequence.threePrimeSticky) {
      return STICKY;
    } else if (sequence.threePrimeOverlap) {
      return OVERLAP;
    }
  }
  return null;
}

function getRightHeaderContents(endpointType: string | null): string | null {
  if (endpointType === STICKY) {
    return 'Right Sticky';
  } else if (endpointType === OVERLAP) {
    return 'Right Overlap';
  }
  return null;
}

function getLeftCellContents(sequence: BulkAssemblyConstants.Sequence, type: string | null): string | null {
  if (type === STICKY) {
    return sequence.fivePrimeSticky;
  } else if (type === OVERLAP) {
    return sequence.fivePrimeOverlap;
  } else {
    return null;
  }
}

function getRightCellContents(sequence: BulkAssemblyConstants.Sequence, type: string | null): string | null {
  if (type === STICKY) {
    return sequence.threePrimeSticky;
  } else if (type === OVERLAP) {
    return sequence.threePrimeOverlap;
  } else {
    return null;
  }
}

function renderSequenceVisualization(
  sequence: BulkAssemblyConstants.SequenceSummary,
  color: string
): JSX.Element {
  return (
    <>
      {sequence.fivePrimeDisplayInfo && sequence.threePrimeDisplayInfo && (
        <FragmentVisualization
          sequence={{
            start: sequence.start,
            end: sequence.end,
            sequenceLength: sequence.sequenceLength,
            strand: sequence.strand,
            circular: sequence.circular,
            fivePrimeDisplayInfo: sequence.fivePrimeDisplayInfo,
            threePrimeDisplayInfo: sequence.threePrimeDisplayInfo,
          }}
          fragmentColor={color}
        />
      )}
      {sequence.error && <span className="text-danger">{sequence.error}</span>}
    </>
  );
}

export default function SlotSummary({
  assembly,
  slotIdx,
  onOpenPrimerSettings,
}: {
  assembly: BulkAssemblyConstants.LocalAssembly;
  slotIdx: number;
  onOpenPrimerSettings: () => void;
}): JSX.Element {
  const errors = BulkAssemblyUtil.getSlotErrors(assembly, slotIdx);
  const slotSummary = assembly.meta.summary && assembly.meta.summary.slots[slotIdx];
  const leftType = slotSummary ? getLeftType(slotSummary.sequences) : null;
  const rightType = slotSummary ? getRightType(slotSummary.sequences) : null;
  const warnings = slotSummary ? slotSummary.warnings : [];
  return (
    <div>
      {errors.length > 0 && (
        <ErrorMessages>
          {errors.map((error, i) => (
            <ErrorMessage key={i}>{error}</ErrorMessage>
          ))}
        </ErrorMessages>
      )}
      {warnings.length > 0 && (
        <Warnings>
          {BulkAssemblyConstants.warningText}
          {warnings.map((warning, i) => (
            <Warning key={i}>{warning}</Warning>
          ))}
        </Warnings>
      )}
      <GibsonPrimerSummary
        assembly={assembly}
        slotIdx={slotIdx}
        onOpenPrimerSettings={onOpenPrimerSettings}
      />
      <ValidationStatus assembly={assembly} />
      {slotSummary && slotSummary.sequences.length > 0 && (
        <div className="bulkAssemblyEditor-slotSummary-tableWrapper">
          <Table numRows={slotSummary.sequences.length}>
            <Column
              header={<Cell>Sequence</Cell>}
              renderCell={(i: number) => <Cell>{slotSummary.sequences[i].name}</Cell>}
            />
            <Column
              header={
                <Cell className="bulkAssemblyEditor-slotSummary-fragmentCell u-overflow-wrap-break-word">
                  Fragment
                </Cell>
              }
              renderCell={(i: number) => (
                <Cell className="bulkAssemblyEditor-slotSummary-fragmentCell u-overflow-wrap-break-word">
                  {renderSequenceVisualization(
                    slotSummary.sequences[i],
                    BulkAssemblyUtil.getSlotFragmentColor(assembly, slotIdx)
                  )}
                </Cell>
              )}
            />
            <Column
              header={<Cell>{getLeftHeaderContents(leftType)}</Cell>}
              renderCell={(i: number) => (
                <Cell>{getLeftCellContents(slotSummary.sequences[i], leftType)}</Cell>
              )}
            />
            <Column
              header={<Cell>Length</Cell>}
              renderCell={(i: number) => <Cell>{slotSummary.sequences[i].length}</Cell>}
            />
            <Column
              header={<Cell>{getRightHeaderContents(rightType)}</Cell>}
              renderCell={(i: number) => (
                <Cell>{getRightCellContents(slotSummary.sequences[i], rightType)}</Cell>
              )}
            />
          </Table>
        </div>
      )}
    </div>
  );
}

SlotSummary.propTypes = {
  assembly: PropTypes.object.isRequired,
  slotIdx: PropTypes.number.isRequired,
};
